// src/App.js
// UPDATED: 2026-08-11 — pass onHUDLogin callback to HUDWrapper so isHUDOnly
// updates immediately after Laura logs in without requiring a page refresh.
// UPDATED: 2026-08-20 — added /rpa/agent-governance route for the system-
// admin-only Token Cost Governance dashboard. Nav link is now gated on
// isSystemAdmin (user.systemRole === 'system_admin'), surfaced via
// authCheck.js + actions/auth.js checkSession(). The backend route remains
// the real access control (403s for non-system-admins, component shows an
// access-denied state) — this only controls whether the link is shown.
// NOTE: systemRole only lands in Redux after AuthWrapper's checkSession()
// runs (page load / refresh). AdminLogin.js does not currently dispatch to
// Redux on login, so a system_admin who just logged in won't see this link
// until the next refresh — same pre-existing gap as the other admin-only
// nav links, not something new here.
import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Home from './Home';
import Login from './Login';
import Register from './Register';
import ForumPage from './components/ForumPage';
import AdminLogin from './components/AdminLogin';
import AdminRegister from './components/AdminRegister';
import AdminVerify from './components/AdminVerify';
import AdminDashboard from './components/AdminDashboard';
import ChatbotMini from './components/ChatbotMini';
import AuthWrapper from './components/AuthWrapper';
import PrivateRoute from './components/PrivateRoute';
import RpaDashboard from './components/rpa/RpaDashboard';
import CMMCDashboard from './components/cmmc/CMMCDashboard';
import HUDWrapper from './components/HUD/HUDWrapper';
import RpaAgentChatMini from './components/rpa/agent/RpaAgentChatMini';
import GovernanceAgentChatMini from './components/rpa/agent/GovernanceAgentChatMini';
import AgentGovernanceDashboard from './components/rpa/agent/AgentGovernanceDashboard';
import HUDScriptAssistant from './components/HUD/HUDScriptAssistant';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const getAccountTypeFromUser = (user) => {
  if (!user || typeof user !== 'object') return null;
  return user.accountType || user.account_type || user.role || user.userType || user.type || null;
};
const getAccountType = (auth) => {
  const user = auth?.user;
  return auth?.accountType || auth?.account_type || getAccountTypeFromUser(user) ||
    getAccountTypeFromUser(user?.user) || getAccountTypeFromUser(user?.admin) ||
    getAccountTypeFromUser(user?.data?.user) || getAccountTypeFromUser(user?.data?.admin) || null;
};
const hasUserIdentity = (user) => {
  if (!user || typeof user !== 'object') return false;
  return Boolean(user._id || user.id || user.email || user.username || user.accountType ||
    user.account_type || user.role || user.userType || user.type ||
    user.user?._id || user.user?.id || user.user?.email ||
    user.admin?._id || user.admin?.id || user.admin?.email ||
    user.data?.user?._id || user.data?.user?.id || user.data?.user?.email ||
    user.data?.admin?._id || user.data?.admin?.id || user.data?.admin?.email);
};
const isAdminAccount = (accountType) => String(accountType || '').trim().toLowerCase() === 'admin';

const App = () => {
  const dispatch  = useDispatch();
  const location  = useLocation();
  const auth      = useSelector((state) => state.auth || {});
  const rpaAgent  = useSelector((state) => state.rpaAgent || {});
  const user      = auth.user || null;
  const accountType     = getAccountType(auth);
  const isAuthenticated = Boolean(auth.isAuthenticated || auth.authenticated || hasUserIdentity(user));
  const isAdmin         = isAdminAccount(accountType);
  const isSystemAdmin   = isAdmin && String(user?.systemRole || '').trim().toLowerCase() === 'system_admin';
  const { selectedDocId, selectedProjectId } = rpaAgent;

  // ⭐ Logs the values that actually gate the nav bar / routes, so a missing
  // link can be traced to "Redux state is wrong" (check [AuthReducer] /
  // [AuthWrapper] logs) vs "App.js derived it wrong" (check this log) in
  // one step, without guessing.
  useEffect(() => {
    console.log('[App] auth state →', {
      isAuthenticated, accountType, isAdmin, isSystemAdmin,
      systemRole: user?.systemRole ?? '(none)',
      userId: user?.id || user?._id || '(none)',
    });
  }, [isAuthenticated, accountType, isAdmin, isSystemAdmin, user]);

  const [isHUDOnly, setIsHUDOnly] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/hud/check-session`, { withCredentials: true })
      .then(res => {
        if (res.data.isAuthenticated && res.data.user?.role === 'hud_analyst') {
          setIsHUDOnly(true);
        } else {
          setIsHUDOnly(false);
        }
      })
      .catch(() => setIsHUDOnly(false));
  }, []);

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    dispatch({ type: 'CLEAR_RPA_AGENT_CONTEXT' });
  };

  const chatbotOnlyRoutes = ['/login', '/register'];
  const renderChatbotRoute = () => {
    switch (location.pathname) {
      case '/login':    return <Login />;
      case '/register': return <Register />;
      default:          return null;
    }
  };

  // /rpa/agent-governance is a PREFIX of /rpa, so it must be tested first and
  // excluded from isRpaRoute. Otherwise the governance page mounts the audit
  // agent — which looks right but hands the user an agent whose entire
  // vocabulary is document-scoped, on a page that has no selected document.
  const isGovernanceRoute = location.pathname.startsWith('/rpa/agent-governance');
  const isRpaRoute = location.pathname.startsWith('/rpa') && !isGovernanceRoute;
  const isHudRoute = location.pathname.startsWith('/hud');

  if (isHUDOnly === null) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: 'Arial, sans-serif',
        color: '#888780', fontSize: '13px', background: '#F7F6F2',
      }}>
        Loading...
      </div>
    );
  }

  if (isHUDOnly && !isHudRoute) {
    return <Navigate to="/hud" replace />;
  }

  return (
    <AuthWrapper>
      <div className="App">
        <nav>
          <ul>
            <li><Link to="/">Home</Link></li>
            {!isAuthenticated ? (
              <>
                <li><Link to="/forum">Forum</Link></li>
                <li><Link to="/register">Register</Link></li>
                <li><Link to="/admin/register">Admin Register</Link></li>
              </>
            ) : (
              <>
                <li><Link to="/forum">Forum</Link></li>
                {isAdmin && (
                  <>
                    <li><Link to="/admin-dashboard">Admin Dashboard</Link></li>
                    <li><Link to="/rpa">RPA Dashboard</Link></li>
                    <li><Link to="/cmmc">CMMC Tracker</Link></li>
                    <li><Link to="/hud">HUD Analytics</Link></li>
                    {isSystemAdmin && (
                      <li><Link to="/rpa/agent-governance">AI Cost Governance</Link></li>
                    )}
                  </>
                )}
                <li><button type="button" onClick={handleLogout}>Logout</button></li>
              </>
            )}
          </ul>
        </nav>

        {!chatbotOnlyRoutes.includes(location.pathname) && (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/forum" element={<ForumPage />} />
            <Route path="/admin/form/11" element={<AdminLogin />} />
            <Route path="/admin/register" element={<AdminRegister />} />
            <Route path="/admin/verify" element={<AdminVerify />} />
            <Route path="/admin-dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
            <Route path="/rpa/agent-governance" element={<PrivateRoute><AgentGovernanceDashboard /></PrivateRoute>} />
            <Route path="/rpa/*" element={<PrivateRoute><RpaDashboard /></PrivateRoute>} />
            <Route path="/cmmc" element={<PrivateRoute><CMMCDashboard /></PrivateRoute>} />
            <Route path="/hud" element={
              <HUDWrapper onHUDLogin={() => setIsHUDOnly(true)} />
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}

        {isGovernanceRoute ? (
          <GovernanceAgentChatMini />
        ) : isRpaRoute ? (
          <RpaAgentChatMini selectedDocId={selectedDocId} selectedProjectId={selectedProjectId} />
        ) : isHudRoute ? (
          <HUDScriptAssistant />
        ) : (
          <ChatbotMini>{renderChatbotRoute()}</ChatbotMini>
        )}
      </div>
    </AuthWrapper>
  );
};

export default App;